import { useState } from "react";
import { BellRing, FileCheck, Loader2, Send } from "lucide-react";

import type { SignatureStatus } from "@/api/db/connection";
import type { PersonDetail } from "@/api/db/people";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Button } from "@/components/ui/button";
import { useSignatureCancel, useSignatureList, useSignatureResend } from "@/hooks/use-signatures";
import { DocumentosLabels } from "@/lib/labels/documentos";
import { cn } from "@/lib/utils";

import { SendTermsModal } from "./send-terms-modal";

const STATUS_CHIP: Record<SignatureStatus, string> = {
	sent: "border-warning/40 bg-warning/10 text-warning",
	signed: "border-whatsapp/40 bg-whatsapp/10 text-whatsapp",
	refused: "border-destructive/40 bg-destructive/10 text-destructive",
	canceled: "border-border bg-background/60 text-muted-foreground",
	expired: "border-border bg-background/60 text-muted-foreground",
};

export function FichaDocumentos({ person }: { person: PersonDetail }) {
	const { data, isError } = useSignatureList(person.id);

	const [sending, setSending] = useState(false);

	return (
		<section data-component="ficha-documentos" className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="type-section-title text-foreground">{DocumentosLabels.title}</h2>
				</div>

				<Button
					type="button"
					data-slot="send-terms"
					variant="default"
					size="sm"
					onClick={() => setSending(true)}
				>
					<Send />
					{DocumentosLabels.send}
				</Button>
			</div>

			{isError && <p className="text-sm text-destructive">{DocumentosLabels.loadError}</p>}

			{data !== undefined && data.length === 0 && (
				<div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
					<p className="text-sm font-medium text-foreground">{DocumentosLabels.empty.title}</p>
				</div>
			)}

			<div className="flex flex-col gap-3">
				{data?.map((process) => (
					<ProcessCard key={process.id} process={process} />
				))}
			</div>

			{sending && <SendTermsModal person={person} onClose={() => setSending(false)} />}
		</section>
	);
}

function ProcessCard({
	process,
}: {
	process: NonNullable<ReturnType<typeof useSignatureList>["data"]>[number];
}) {
	const { mutate: resend, isPending: resending } = useSignatureResend();
	const { mutate: cancel, isPending: canceling } = useSignatureCancel();

	return (
		<article data-slot="signature-process" className="surface-panel flex flex-col gap-2 p-4">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span
					className={cn(
						"inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
						STATUS_CHIP[process.status],
					)}
				>
					{DocumentosLabels.status[process.status]}
				</span>

				{process.status === "sent" && (
					<div className="flex items-center gap-2">
						<Button
							type="button"
							data-slot="signature-resend"
							variant="secondary"
							size="sm"
							disabled={resending}
							onClick={() => resend({ id: process.id })}
						>
							{resending && <Loader2 className="animate-spin" />}
							{!resending && <BellRing />}
							{DocumentosLabels.resendAction}
						</Button>

						<ConfirmationDialog
							slot="signature-cancel"
							tone="destructive"
							title={DocumentosLabels.cancelPrompt}
							pendingLabel={DocumentosLabels.cancelAction}
							isPending={canceling}
							onConfirm={() => cancel({ id: process.id })}
							className="rounded-md px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							{DocumentosLabels.cancelAction}
						</ConfirmationDialog>
					</div>
				)}
			</div>

			<ul className="flex flex-col gap-0.5">
				{process.documents.map((document) => (
					<li
						key={document.id}
						className="flex items-center gap-2 text-sm font-medium text-foreground"
					>
						{document.term_name}
						{document.signed_file !== null && (
							<a
								data-slot="signature-download"
								href={`/signatures/file/${document.id}`}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
							>
								<FileCheck className="size-3.5" />
								{DocumentosLabels.downloadSigned}
							</a>
						)}
					</li>
				))}
			</ul>

			<p className="text-xs text-muted-foreground">
				{DocumentosLabels.sentAt(process.created_at)} por {process.author}
				<span className="mx-1.5 border-l border-border" aria-hidden />
				{DocumentosLabels.deadline(process.deadline_at)}
			</p>
		</article>
	);
}
